(function (global) {
  "use strict";

  const PRODUCT_CONTENT_QUERY = `{
    "sizeGuide": *[
      _type == "sizeGuide" &&
      references(*[_type == "product" && store.gid == $productGid]._id)
    ] | order(_updatedAt desc)[0]{
      _id,
      title,
      notes,
      sizes,
      columns[]{
        _key,
        title,
        values[]{
          _key,
          value,
          unit,
          textOverride
        }
      },
      graphic{
        asset->{
          url,
          metadata{
            dimensions{
              width,
              height
            }
          }
        },
        crop,
        hotspot,
        purpose,
        alt
      }
    },
    "shopTheLook": *[
      _type == "product" &&
      store.gid == $productGid
    ][0]{
      "items": shopTheLook[]->{
        "handle": store.slug.current,
        "gid": store.gid,
        "status": store.status,
        "isDeleted": store.isDeleted
      }
    },
    "modelSizing": *[
      _type == "product" &&
      store.gid == $productGid
    ][0].modelSizing
  }`;

  const STOCKISTS_QUERY = `*[
    _type == "stockist"
  ] | order(name asc, city asc, country asc, phone asc, _id asc){
    _id,
    name,
    phone,
    city,
    country
  }`;

  const SHOP_THE_LOOK_LIMIT = 6;

  const LEGACY_SIMPLE_MEASUREMENT_PATTERN =
    /^-?\d+(?:\.\d+)?(?:\s*[-–]\s*-?\d+(?:\.\d+)?)?$/;

  function cleanString(value) {
    return typeof value === "string" ? value.trim() : "";
  }

  function hasBalancedDelimiters(value) {
    const pairs = {
      ")": "(",
      "]": "[",
      "}": "{",
    };
    const openingDelimiters = new Set(Object.values(pairs));
    const stack = [];

    for (const character of value) {
      if (openingDelimiters.has(character)) {
        stack.push(character);
        continue;
      }

      if (
        Object.prototype.hasOwnProperty.call(pairs, character) &&
        stack.pop() !== pairs[character]
      ) {
        return false;
      }
    }

    return stack.length === 0;
  }

  function readConfig(element) {
    const dataset = element?.dataset || {};
    const projectId = cleanString(dataset.sanityProjectId);
    const datasetName = cleanString(dataset.sanityDataset);
    const apiVersion = cleanString(dataset.sanityApiVersion);

    if (!projectId || !datasetName || !/^\d{4}-\d{2}-\d{2}$/.test(apiVersion)) {
      return null;
    }

    return { projectId, dataset: datasetName, apiVersion };
  }

  function buildQueryUrl(config, query, parameters) {
    if (!config?.projectId || !config?.dataset || !config?.apiVersion) {
      throw new Error("Sanity storefront configuration is incomplete.");
    }

    const endpoint = new URL(
      `https://${config.projectId}.apicdn.sanity.io/v${config.apiVersion}/data/query/${encodeURIComponent(config.dataset)}`
    );

    endpoint.searchParams.set("perspective", "published");
    endpoint.searchParams.set("query", query);
    endpoint.searchParams.set("returnQuery", "false");

    Object.entries(parameters || {}).forEach(([name, value]) => {
      endpoint.searchParams.set(`$${name}`, JSON.stringify(value));
    });

    return endpoint.toString();
  }

  const productContentRequests = new Map();

  function fetchProductContent(config, productGid) {
    const url = buildQueryUrl(config, PRODUCT_CONTENT_QUERY, { productGid });

    if (!productContentRequests.has(url)) {
      const request = global
        .fetch(url, {
          headers: {
            Accept: "application/json",
          },
        })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Sanity request failed with ${response.status}`);
          }

          return response.json();
        })
        .then((data) => data?.result || {});

      request.catch(() => productContentRequests.delete(url));
      productContentRequests.set(url, request);
    }

    return productContentRequests.get(url);
  }

  function normalizeGraphic(value) {
    const assetUrl = cleanString(value?.asset?.url);
    const width = Number(value?.asset?.metadata?.dimensions?.width);
    const height = Number(value?.asset?.metadata?.dimensions?.height);
    const purpose = value?.purpose;
    const alt = cleanString(value?.alt);
    let parsedAssetUrl = null;

    try {
      parsedAssetUrl = new URL(assetUrl);
    } catch (_error) {
      return null;
    }

    if (
      !assetUrl ||
      parsedAssetUrl.protocol !== "https:" ||
      parsedAssetUrl.hostname !== "cdn.sanity.io" ||
      !Number.isFinite(width) ||
      width <= 0 ||
      !Number.isFinite(height) ||
      height <= 0 ||
      (purpose !== "informative" && purpose !== "decorative") ||
      (purpose === "informative" && !alt)
    ) {
      return null;
    }

    return {
      asset: {
        url: assetUrl,
        width,
        height,
      },
      crop: value.crop || null,
      hotspot: value.hotspot || null,
      purpose,
      alt: purpose === "decorative" ? "" : alt,
    };
  }

  function formatMeasurement(value, unit) {
    if (!Number.isFinite(value) || (unit !== "in" && unit !== "cm")) {
      return null;
    }

    return `${String(value)} ${unit}`;
  }

  function formatTextMeasurement(value, unit) {
    const text = cleanString(value);

    if (!text || !hasBalancedDelimiters(text)) {
      return null;
    }

    const legacyUnit = unit === "in" || unit === "cm" ? unit : "";

    if (LEGACY_SIMPLE_MEASUREMENT_PATTERN.test(text)) {
      return legacyUnit ? `${text} ${legacyUnit}` : null;
    }

    return text;
  }

  function normalizeSizeGuide(value) {
    if (!value || typeof value !== "object") {
      return null;
    }

    const title = cleanString(value.title);
    const notes = cleanString(value.notes);
    const sizes = Array.isArray(value.sizes) ? value.sizes.map(cleanString) : [];

    if (
      !title ||
      sizes.length === 0 ||
      sizes.some((size) => !size) ||
      new Set(sizes).size !== sizes.length ||
      !Array.isArray(value.columns) ||
      value.columns.length === 0
    ) {
      return null;
    }

    const columns = [];

    for (const column of value.columns) {
      const columnTitle = cleanString(column?.title);

      if (
        !columnTitle ||
        !Array.isArray(column?.values) ||
        column.values.length !== sizes.length
      ) {
        return null;
      }

      const values = [];

      for (const measurement of column.values) {
        const numericValue = measurement?.value;
        const unit = measurement?.unit;
        const textOverride = cleanString(measurement?.textOverride);
        const formattedValue = formatMeasurement(numericValue, unit);
        const formattedText = formatTextMeasurement(textOverride, unit);

        if (!formattedText && !formattedValue) {
          return null;
        }

        values.push({
          key: cleanString(measurement?._key),
          value: numericValue,
          unit,
          textOverride,
          display: formattedText || formattedValue,
        });
      }

      columns.push({
        key: cleanString(column?._key),
        title: columnTitle,
        values,
      });
    }

    return {
      id: cleanString(value._id),
      title,
      notes,
      sizes,
      columns,
      graphic: normalizeGraphic(value.graphic),
    };
  }

  function normalizeModelSizing(value) {
    return cleanString(value);
  }

  function buildImageUrl(graphic, requestedWidth) {
    if (!graphic?.asset?.url || !graphic.asset.width || !graphic.asset.height) {
      return null;
    }

    const imageUrl = new URL(graphic.asset.url);
    const crop = graphic.crop || {};
    const left = Math.round((Number(crop.left) || 0) * graphic.asset.width);
    const top = Math.round((Number(crop.top) || 0) * graphic.asset.height);
    const right = Math.round((Number(crop.right) || 0) * graphic.asset.width);
    const bottom = Math.round((Number(crop.bottom) || 0) * graphic.asset.height);
    const croppedWidth = Math.max(1, graphic.asset.width - left - right);
    const croppedHeight = Math.max(1, graphic.asset.height - top - bottom);

    if (left || top || right || bottom) {
      imageUrl.searchParams.set(
        "rect",
        `${left},${top},${croppedWidth},${croppedHeight}`
      );
    }

    imageUrl.searchParams.set("w", String(requestedWidth || 600));
    imageUrl.searchParams.set("fit", "max");
    imageUrl.searchParams.set("auto", "format");

    return imageUrl.toString();
  }

  function normalizeStockists(value) {
    if (!Array.isArray(value)) {
      throw new TypeError("Sanity Stockists result must be an array.");
    }

    const stockists = value
      .map((stockist) => {
        const normalized = {
          id: cleanString(stockist?._id),
          name: cleanString(stockist?.name),
          phone: cleanString(stockist?.phone),
          city: cleanString(stockist?.city),
          country: cleanString(stockist?.country),
        };

        return {
          ...normalized,
          missingFields: ["name", "phone", "city", "country"].filter(
            (field) => !normalized[field]
          ),
        };
      })
      .sort(
        (left, right) =>
          left.name.localeCompare(right.name, undefined, {
            sensitivity: "base",
          }) ||
          left.city.localeCompare(right.city, undefined, {
            sensitivity: "base",
          }) ||
          left.country.localeCompare(right.country, undefined, {
            sensitivity: "base",
          }) ||
          left.phone.localeCompare(right.phone, undefined, {
            sensitivity: "base",
          }) ||
          left.id.localeCompare(right.id)
      );

    const incomplete = stockists
      .filter((stockist) => stockist.missingFields.length > 0)
      .map(({ id, name, phone, city, country, missingFields }) => ({
        id,
        name,
        phone,
        city,
        country,
        missingFields,
      }));

    return {
      total: stockists.length,
      complete: stockists.length - incomplete.length,
      incomplete,
      stockists: stockists.map(({ missingFields, ...stockist }) => stockist),
    };
  }

  function normalizeShopTheLook(value, currentGid) {
    const items = Array.isArray(value?.items) ? value.items : [];
    const seen = new Set();
    const handles = [];

    for (const item of items) {
      const handle = cleanString(item?.handle);
      const gid = cleanString(item?.gid);

      if (
        !handle ||
        item.status !== "active" ||
        item.isDeleted === true ||
        (gid && gid === currentGid) ||
        seen.has(handle)
      ) {
        continue;
      }

      seen.add(handle);
      handles.push(handle);

      if (handles.length === SHOP_THE_LOOK_LIMIT) {
        break;
      }
    }

    return handles;
  }

  global.GregRossSanity = Object.freeze({
    PRODUCT_CONTENT_QUERY,
    STOCKISTS_QUERY,
    buildImageUrl,
    buildQueryUrl,
    fetchProductContent,
    formatMeasurement,
    normalizeModelSizing,
    normalizeShopTheLook,
    normalizeSizeGuide,
    normalizeStockists,
    readConfig,
  });
})(typeof window === "undefined" ? globalThis : window);
